/* eslint-disable @next/next/no-img-element */
import React, { useContext, useState, useMemo, useEffect } from "react";
import { GetServerSideProps } from "next";
import { LogContext } from "context/log";
import cx from "classnames";
import st from "./index.module.scss";

import { endpoints } from "config";
import CollectionItem from "components/CollectionItem";
import RockstarHeader from "components/RockstarHeader";
import { useGetItemsQuery } from "services/graphql/generated";
import { convertFromCentsToDollars } from "helpers/currency";
import RockstarFooter from "components/RockstarFooter";
import Link from "next/link";
import { WpYoastHead } from "types/wp-yoast-head";
import HeadTagsRenderer from "components/HeadTagsRenderer";
import { DatoProductFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import TopCtaBanner from "components/TopCtaBanner";
import { usePageParams } from "hooks/usePageParams";
import { SelectDropdown } from "components/RockstarDropdown/SelectDropdown";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { generatePageId } from "@pepdirect/helpers/analyticsLogger";

interface KitsPageProps {
  products: DatoProductFragment[];
}

enum SublineFilters {
  All = "All",
  Original = "Original",
  "Pure Zero" = "Pure Zero",
  Punched = "Punched",
  Recovery = "Recovery",
  Starfield = "Starfield",
  XDurance = "XDurance",
  Unplugged = "Unplugged",
  Boom = "Boom",
  Throwback = "Throwback",
}

enum NutritionFilters {
  All = "All",
  "0 Sugar" = "0 Sugar",
  "0 Calories" = "0 Calories",
  "0 Carbs" = "0 Carbs",
  "Hemp Seed Oil" = "Hemp Seed Oil",
}

enum CanSizeFilters {
  All = "All",
  "16OZ" = "16OZ",
  "12OZ" = "12OZ",
}

enum UnavailableProductsFilters {
  hide = "hide",
  show = "show",
}

enum FilterKeys {
  subline = "subline",
  nutrition = "nutrition",
  canSize = "canSize",
  unAvailableProducts = "unAvailableProducts",
}

interface CategoryFilter {
  [FilterKeys.subline]: SublineFilters;
  [FilterKeys.nutrition]: NutritionFilters;
  [FilterKeys.canSize]: CanSizeFilters;
  [FilterKeys.unAvailableProducts]: UnavailableProductsFilters;
}

export const getServerSideProps: GetServerSideProps<KitsPageProps> = async (
  appContext
) => {
  try {
    const { allProducts } = await getSdk(
      createDatoClient(appContext.preview)
    ).products();

    return {
      props: {
        products: allProducts,
      },
    };
  } catch (error) {
    return {
      props: {
        products: [],
      },
    };
  }
};

type Item = {
  __typename?: "Item";
  gtin: string;
  price?: number | null;
  isOutOfStock?: boolean | null;
  inventoryQty?: number | null;
};

const initialPageParams: Record<string, string> = {
  subline: "All",
  nutrition: "All",
  canSize: "All",
};

export default function Kit({ products }: KitsPageProps): JSX.Element {
  const [pageParams, setPageParams] = usePageParams(initialPageParams);
  const HIDE_UNAVAILABLE_PRODUCTS = "hideUnavailableProducts";
  const { data: allPepdirectItemsQuery } = useGetItemsQuery();
  const { useLogPageView } = useContext(LogContext);
  const [filterVals, setFilterVals] = useState<CategoryFilter>({
    [FilterKeys.subline]:
      (pageParams?.subline as SublineFilters) || SublineFilters.All,
    [FilterKeys.nutrition]:
      (pageParams?.nutrition as NutritionFilters) || NutritionFilters.All,
    [FilterKeys.canSize]:
      (pageParams?.canSize as CanSizeFilters) || CanSizeFilters.All,
    [FilterKeys.unAvailableProducts]: UnavailableProductsFilters.show,
  });

  const itemsByGtin = useMemo(() => {
    const items: { [key: string]: Item } = {};
    allPepdirectItemsQuery?.getItems?.items.forEach((item) => {
      items[item.gtin] = item;
    });
    return items;
  }, [allPepdirectItemsQuery]);

  useEffect(() => {
    setPageParams({
      ...filterVals,
    });
  }, [filterVals, setPageParams]);

  useEffect(() => {
    const unAvailableProducts = localStorage.getItem(HIDE_UNAVAILABLE_PRODUCTS);
    if (unAvailableProducts) {
      setFilterVals((oldValues) => ({
        ...oldValues,
        [FilterKeys.unAvailableProducts]:
          unAvailableProducts as UnavailableProductsFilters,
      }));
    }
  }, []);

  const onFilterChange = (
    key: FilterKeys,
    val:
      | SublineFilters
      | NutritionFilters
      | CanSizeFilters
      | UnavailableProductsFilters
  ) => {
    if (key === FilterKeys.unAvailableProducts) {
      localStorage.setItem(HIDE_UNAVAILABLE_PRODUCTS, val);
    }
    setFilterVals((oldValues) => ({
      ...oldValues,
      [key]: val,
    }));
  };

  const shouldShowClearAllButton =
    filterVals?.[FilterKeys.subline] !== SublineFilters.All ||
    filterVals?.[FilterKeys.nutrition] !== NutritionFilters.All ||
    filterVals?.[FilterKeys.canSize] !== CanSizeFilters.All;

  const filteredProducts = useMemo(() => {
    let allProducts = products;

    // FILTER BY SUBLINE
    if (filterVals[FilterKeys.subline] !== SublineFilters.All) {
      allProducts = allProducts.filter(
        (product) =>
          product?.flavorName?.toLowerCase() ===
          filterVals?.[FilterKeys.subline].toLowerCase()
      );
    }

    // FILTER BY NUTRITION
    if (filterVals[FilterKeys.nutrition] !== NutritionFilters.All) {
      allProducts = allProducts.filter((product) => {
        if (product.nutritionFacts?.length) {
          return product.nutritionFacts?.some((nut) => {
            return (
              nut?.value?.toLowerCase() ===
              filterVals?.[FilterKeys.nutrition]?.toLowerCase()
            );
          });
        }
      });
    }
    // FILTER BY CAN SIZE
    if (filterVals[FilterKeys.canSize] !== CanSizeFilters.All) {
      allProducts = allProducts.filter(
        (product) =>
          product?.canSize?.toLowerCase() ===
          filterVals?.[FilterKeys.canSize]?.toLowerCase()
      );
    }

    if (
      filterVals[FilterKeys.unAvailableProducts] ===
      UnavailableProductsFilters.hide
    ) {
      allProducts = allProducts.filter((product) => {
        return (
          product.gtin &&
          !itemsByGtin[product.gtin]?.isOutOfStock &&
          !!itemsByGtin[product.gtin]?.inventoryQty
        );
      });
    }
    return allProducts;
  }, [filterVals, itemsByGtin, products]);

  const nutritionOptions = useMemo(
    () => [
      { value: NutritionFilters.All, label: NutritionFilters.All },
      {
        value: NutritionFilters["0 Sugar"],
        label: NutritionFilters["0 Sugar"],
      },
      {
        value: NutritionFilters["0 Calories"],
        label: NutritionFilters["0 Calories"],
      },
      {
        value: NutritionFilters["0 Carbs"],
        label: NutritionFilters["0 Carbs"],
      },
      {
        value: NutritionFilters["Hemp Seed Oil"],
        label: NutritionFilters["Hemp Seed Oil"],
      },
    ],
    []
  );

  const sublineOptions = useMemo(
    () => [
      { value: SublineFilters.All, label: SublineFilters.All },
      { value: SublineFilters.Original, label: SublineFilters.Original },
      {
        value: SublineFilters["Pure Zero"],
        label: SublineFilters["Pure Zero"],
      },
      { value: SublineFilters.Punched, label: SublineFilters.Punched },
      { value: SublineFilters.Recovery, label: SublineFilters.Recovery },
      { value: SublineFilters.Starfield, label: SublineFilters.Starfield },
      { value: SublineFilters.XDurance, label: SublineFilters.XDurance },
      { value: SublineFilters.Unplugged, label: SublineFilters.Unplugged },
      { value: SublineFilters.Boom, label: SublineFilters.Boom },
      { value: SublineFilters.Throwback, label: SublineFilters.Throwback },
    ],
    []
  );

  const canSizeOptions = useMemo(
    () => [
      { value: CanSizeFilters.All, label: CanSizeFilters.All },
      { value: CanSizeFilters["16OZ"], label: CanSizeFilters["16OZ"] },
      { value: CanSizeFilters["12OZ"], label: CanSizeFilters["12OZ"] },
    ],
    []
  );

  const seoTags: WpYoastHead = {
    title: "Shop All - Rockstar Energy",
    description:
      "There's a Rockstar for every hustle. Find the latest and greatest at Rockstar, from old favorites to new flavors!",
    canonical: `${endpoints.web}/shop-all/`,
    og_locale: "en_US",
    og_type: "article",
    og_title: "Shop All - Rockstar Energy",
    og_description:
      "There's a Rockstar for every hustle. Find the latest and greatest at Rockstar, from old favorites to new flavors!",
    og_url: `${endpoints.web}/shop-all/`,
    og_site_name: "Rockstar Energy",
    og_image: [
      {
        url: `https://www.datocms-assets.com/74813/1658320467-slide-plp-og.webp`,
      },
    ],
    twitter_card: "summary_large_image",
  };

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.collection,
    pageCategory: "all products",
    pageTitle: seoTags.title || "",
    id: generatePageId("shop-all"),
  });

  return (
    <>
      <HeadTagsRenderer yoastJson={seoTags} />
      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              <div className="category-page">
                <div className="categories-section">
                  <div className="container">
                    <div className="rockstar-product-list">
                      <h2 className="rockstar-product-title title">
                        FIND YOUR ROCKSTAR MATCH
                      </h2>
                      <p className={cx(st.quizIntro)}>
                        What Rockstar Energy drink is right for you? Take a
                        short quiz to find a flavor that matches your taste.
                      </p>
                      <Link href="/find-your-flavor">
                        <a
                          href="/find-your-flavor"
                          className={cx("btn", st.btnGold, "btn-cta")}
                        >
                          Take the quiz
                        </a>
                      </Link>
                      <div
                        className={cx(
                          "category-filters",
                          shouldShowClearAllButton && "filter-active"
                        )}
                      >
                        <div className="category-filter flavor-filter">
                          <label>Subline</label>
                          <SelectDropdown
                            onChange={async (val: string) =>
                              onFilterChange(
                                FilterKeys.subline,
                                val as SublineFilters
                              )
                            }
                            selectedOption={{
                              value: filterVals[FilterKeys.subline],
                              label: filterVals[FilterKeys.subline],
                            }}
                            options={sublineOptions}
                          />
                        </div>
                        <div className="category-filter nutrition-filter">
                          <label>Nutrition</label>
                          <SelectDropdown
                            selectedOption={{
                              value: filterVals[FilterKeys.nutrition],
                              label: filterVals[FilterKeys.nutrition],
                            }}
                            onChange={async (val: string) =>
                              onFilterChange(
                                FilterKeys.nutrition,
                                val as NutritionFilters
                              )
                            }
                            options={nutritionOptions}
                          />
                        </div>
                        <div className="category-filter can-size-filter">
                          <label>Can Size</label>
                          <SelectDropdown
                            selectedOption={{
                              value: filterVals[FilterKeys.canSize],
                              label: filterVals[FilterKeys.canSize],
                            }}
                            onChange={async (val: string) =>
                              onFilterChange(
                                FilterKeys.canSize,
                                val as CanSizeFilters
                              )
                            }
                            options={canSizeOptions}
                          />
                        </div>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setFilterVals({
                              [FilterKeys.subline]: SublineFilters.All,
                              [FilterKeys.nutrition]: NutritionFilters.All,
                              [FilterKeys.canSize]: CanSizeFilters.All,
                              [FilterKeys.unAvailableProducts]:
                                UnavailableProductsFilters.show,
                            });
                            localStorage.setItem(
                              HIDE_UNAVAILABLE_PRODUCTS,
                              UnavailableProductsFilters.show
                            );
                          }}
                          title="clear-all"
                          className="clear-all"
                          style={{
                            ...(shouldShowClearAllButton && {
                              display: "inline",
                            }),
                          }}
                        >
                          Clear All
                        </a>
                      </div>
                      <div className="checkbox-container">
                        <input
                          id="productToggle"
                          className="plp-checkbox rockstar-checkbox"
                          checked={
                            filterVals.unAvailableProducts ===
                            UnavailableProductsFilters.hide
                          }
                          onChange={() => {
                            onFilterChange(
                              FilterKeys.unAvailableProducts,
                              filterVals.unAvailableProducts ===
                                UnavailableProductsFilters.show
                                ? UnavailableProductsFilters.hide
                                : UnavailableProductsFilters.show
                            );
                          }}
                          type="checkbox"
                        ></input>
                        <label
                          htmlFor="productToggle"
                          className="checkbox-text"
                        >
                          <span>
                            Hide Products Unavailable for Purchase on
                            RockstarEnergy.com
                          </span>
                        </label>
                      </div>
                      <ul
                        id="products"
                        className="rockstar-products rockstar-products-ul"
                      >
                        {[...filteredProducts]?.map((product) => {
                          return (
                            <CollectionItem
                              key={product?.id}
                              slug={product?.slug || ""}
                              image={product?.productImages?.[0]?.url}
                              flavorName={product?.flavorName || ""}
                              title={product?.title || ""}
                              nutritionLink={product?.nutritionLink || ""}
                              price={(() => {
                                const currItem = allPepdirectItemsQuery?.[
                                  "getItems"
                                ]?.items?.find(
                                  (item) => item.gtin === product?.gtin
                                );
                                return currItem?.price
                                  ? convertFromCentsToDollars(
                                      currItem?.price || 0
                                    )
                                  : "";
                              })()}
                            />
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>{" "}
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
}
